import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { Pagination } from "@/types";
import { OrganizationMembersTableRow } from "@/types/organization-member";
import { handleResponse } from "@/utils/errorResponseHandlers";

export interface SetBreakGlassBody {
  target_user_id: number;
  is_break_glass: boolean;
}

export interface SetBreakGlassResponse {
  organization_id: number;
  user_id: number;
  is_break_glass: boolean;
}

export const useSetBreakGlass = (organizationId: number) => {
  const auth = useAuth();
  const userToken = auth?.userToken;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: SetBreakGlassBody) =>
      fetch(ENDPOINTS.identity.break_glass(organizationId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken ?? ""}`,
        },
        body: JSON.stringify(body),
      }).then(handleResponse) as Promise<SetBreakGlassResponse>,
    onSuccess: (_data, variables) => {
      queryClient.setQueriesData<Pagination<OrganizationMembersTableRow>>(
        { queryKey: [ENDPOINTS.organization_members] },
        old => {
          if (!old?.items) {
            return old;
          }
          return {
            ...old,
            items: old.items.map(item => {
              if ("user_id" in item && item.organization_id === organizationId && item.is_owner) {
                return {
                  ...item,
                  is_break_glass: item.user_id === variables.target_user_id,
                };
              }
              return item;
            }),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: [ENDPOINTS.organization_members] });
      queryClient.invalidateQueries({ queryKey: ["mfa", "policy"] });
    },
  });
};
